import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatPrice(price: number, contractType: 'SALE' | 'RENT'): string {
  const formatted = new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(price);

  if (contractType === 'RENT') {
    return `${formatted}/mese`;
  }

  return formatted;
}

export function formatSurface(surface: number): string {
  return `${surface.toLocaleString('it-IT')} m²`;
}

export function formatDate(date: string | Date): string {
  return new Intl.DateTimeFormat('it-IT', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(date));
}

export function formatRelativeDate(date: string | Date): string {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Oggi';
  } else if (diffDays === 1) {
    return 'Ieri';
  } else if (diffDays < 7) {
    return `${diffDays} giorni fa`;
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} settimana${weeks > 1 ? 'e' : ''} fa`;
  } else if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} mese${months > 1 ? 'i' : ''} fa`;
  } else {
    const years = Math.floor(diffDays / 365);
    return `${years} anno${years > 1 ? 'i' : ''} fa`;
  }
}

export function getPlatformLabel(platform: string): string {
  const labels: Record<string, string> = {
    IMMOBILIARE: 'Immobiliare.it',
    IDEALISTA: 'Idealista',
    CASA: 'Casa.it',
    SUBITO: 'Subito.it',
  };
  return labels[platform] || platform;
}

export function getPropertyTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    APARTMENT: 'Appartamento',
    HOUSE: 'Casa',
    VILLA: 'Villa',
    LOFT: 'Loft',
    STUDIO: 'Monolocale',
    OFFICE: 'Ufficio',
    COMMERCIAL: 'Locale commerciale',
    LAND: 'Terreno',
    GARAGE: 'Box/Garage',
    OTHER: 'Altro',
  };
  return labels[type] || type;
}

export function getContractTypeLabel(type: 'SALE' | 'RENT'): string {
  return type === 'SALE' ? 'Vendita' : 'Affitto';
}

export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => func(...args), wait);
  };
}
